import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./MinglePage.css";
import { GAME_OPTIONS_PATH } from "../../../../constants";
import mingleBackground from "./resources/mingle.jpeg";
import { shuffleArray } from "../../../../utils";
import { FIREBASE_AUTH } from "../../../../FirebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { updateProgress, useGrammarUnitData } from "../../../../database";
import questionImage from "./resources/mingle.jpeg";
import correctImage from "./resources/correctImage.png";
import wrongImage1 from "./resources/wrongImage.png";
import wrongImage2 from "./resources/wrongImage2.png";
import bgm from "./resources/mingle_bgm.mp3";
import AudioControls from "../../GameComponents/AudioControls";

function MinglePage() {
  const EASY_RESPONSE_NUM = 3;
  const MEDIUM_RESPONSE_NUM = 5;
  const HARD_RESPONSE_NUM = 7;
  const location = useLocation();
  const {unit, category} = location.state;

  const [user, setUser] = useState(null);
  const {
    data: grammarData,
    loading,
    error,
  } = useGrammarUnitData(unit.unit, unit.textbook);

  const TIMER_SETTINGS = {
    easy: 20,
    medium: 15,
    hard: 10,
  };

  const [state, setState] = useState({
    questions: [],
    currQuestionIndex: 0,
    score: 0,
    numWrong: 0,
    requiredScore: 0,
    radioButton: null,
    showErrorMessage: false,
    showCorrectMessage: false,
    showSubmit: true,
    currentScreen: "start",
    timeLeft: 20,
    difficulty: "easy",
    isReviewPeriod: false,
    currentImage: questionImage,
  });

  const WRONG_IMAGES = [wrongImage1, wrongImage2];
  const pickRandomWrongImage = () =>
    WRONG_IMAGES[Math.floor(Math.random() * WRONG_IMAGES.length)];
  const [showTutorial, setShowTutorial] = useState(false);

  const audioRefBgm = useRef(null);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (audioRefBgm.current) {
        audioRefBgm.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  useEffect(() => {
     if (state.currentScreen === "game" && audioRefBgm.current) {
        audioRefBgm.current.play().catch(console.error);
     } else if ((state.currentScreen === "end" || state.currentScreen === "start") && audioRefBgm.current) {
        audioRefBgm.current.pause();
        audioRefBgm.current.currentTime = 0;
     }
  }, [state.currentScreen]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, (user) => {
      setUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!loading && grammarData?.length > 0) {
      setState((prevState) => ({
        ...prevState,
        questions: grammarData,
      }));
    }
  }, [grammarData, loading]);

  useEffect(() => {
    let timer;
    if (
      state.currentScreen === "game" &&
      state.timeLeft > 0 &&
      !state.isReviewPeriod
    ) {
      timer = setInterval(() => {
        setState((prevState) => ({
          ...prevState,
          timeLeft: Math.max(prevState.timeLeft - 1, 0),
        }));
      }, 1000);
    } else if (
      state.timeLeft <= 0 &&
      state.currentScreen === "game" &&
      !state.isReviewPeriod
    ) {
      handleTimeUp();
    }

    return () => clearInterval(timer);
  }, [state.currentScreen, state.isReviewPeriod, state.timeLeft]);

  const startGame = () => {
    const difficultyElement = document.querySelector(
      'input[name="difficulty"]:checked',
    );

    if (!difficultyElement) {
      alert("Please select difficulty");
      return;
    }

    const difficulty = difficultyElement.value;
    const requiredScore =
      difficulty === "easy"
        ? EASY_RESPONSE_NUM
        : difficulty === "medium"
          ? MEDIUM_RESPONSE_NUM
          : HARD_RESPONSE_NUM;

    const initialTime = TIMER_SETTINGS[difficulty];

    shuffleArray(grammarData);

    setState((prevState) => ({
      ...prevState,
      currQuestionIndex: 0,
      score: 0,
      numWrong: 0,
      requiredScore: requiredScore,
      radioButton: null,
      showErrorMessage: false,
      showCorrectMessage: false,
      currentScreen: "game",
      timeLeft: initialTime,
      difficulty: difficulty,
      currentImage: questionImage,
    }));
  };

  const handleOptionChange = (event) => {
    setState({ ...state, radioButton: event.target.value });
  };

  const handleTimeUp = () => {
    const wrong = state.numWrong + 1;

    if (user) {
      updateProgress(user.email, unit.unit, unit.textbook, "mingle", false);
    }

    setState((prevState) => ({
      ...prevState,
      numWrong: prevState.numWrong + 1,
      showErrorMessage: true,
      showSubmit: false,
      isReviewPeriod: true,
      currentImage: pickRandomWrongImage(),
    }));

    setTimeout(() => {
      if (wrong === 3) {
        setState((prevState) => ({
          ...prevState,
          currentScreen: "end",
          showErrorMessage: false,
          isReviewPeriod: false,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          showErrorMessage: false,
          showSubmit: true,
          isReviewPeriod: false,
          timeLeft: TIMER_SETTINGS[prevState.difficulty],
          currQuestionIndex:
            (prevState.currQuestionIndex + 1) % prevState.questions.length,
        }));
      }
    }, 3000);
  };

  const submitAnswer = async () => {
    if (!state.questions[state.currQuestionIndex]) {
      console.error("No question available");
      return;
    }

    const isTrueFalse =
      state.questions[state.currQuestionIndex].choices.length === 0;
    const submissionRadioButton = document.querySelector(
      `input[name="${isTrueFalse ? "true_false" : "multi_choice"}"]:checked`,
    );

    if (!submissionRadioButton) {
      alert("Please select an answer before submitting!");
      return;
    }

    try {
      const submission = submissionRadioButton.value;
      const isCorrect =
        submission === state.questions[state.currQuestionIndex].answer;

      setState((prevState) => ({
        ...prevState,
        showCorrectMessage: isCorrect,
        showErrorMessage: !isCorrect,
        showSubmit: false,
        isReviewPeriod: true,
        currentImage: isCorrect ? correctImage : pickRandomWrongImage(),
      }));

      if (isCorrect) {
        const newScore = state.score + 1;

        if (user) {
          await updateProgress(user.email, unit.unit, unit.textbook, "mingle", true);
        }

        setTimeout(() => {
          if (newScore === state.requiredScore) {
            setState((prevState) => ({
              ...prevState,
              score: newScore,
              currentScreen: "end",
              showCorrectMessage: false,
              isReviewPeriod: false,
            }));
          } else {
            setState((prevState) => ({
              ...prevState,
              score: newScore,
              showCorrectMessage: false,
              showSubmit: true,
              isReviewPeriod: false,
              timeLeft: TIMER_SETTINGS[prevState.difficulty],
              radioButton: "",
              currQuestionIndex:
                (prevState.currQuestionIndex + 1) % prevState.questions.length,
              currentImage: questionImage,
            }));
          }
        }, 3000);
      } else {
        const newWrong = state.numWrong + 1;

        if (user) {
          await updateProgress(user.email, unit.unit, unit.textbook, "mingle", false);
        }

        setTimeout(() => {
          if (newWrong === 3) {
            setState((prevState) => ({
              ...prevState,
              numWrong: newWrong,
              currentScreen: "end",
              showErrorMessage: false,
              isReviewPeriod: false,
            }));
          } else {
            setState((prevState) => ({
              ...prevState,
              numWrong: newWrong,
              showErrorMessage: false,
              showSubmit: true,
              isReviewPeriod: false,
              timeLeft: TIMER_SETTINGS[prevState.difficulty],
              radioButton: "",
              currQuestionIndex:
                (prevState.currQuestionIndex + 1) % prevState.questions.length,
              currentImage: questionImage,
            }));
          }
        }, 3000);
      }
    } catch (error) {
      console.error("Error processing answer:", error);
      alert("There was an error processing your answer. Please try again.");
    }
  };

  function playAgain() {
    setState((prevState) => ({
      ...prevState,
      currQuestionIndex: 0,
      score: 0,
      numWrong: 0,
      radioButton: "",
      showErrorMessage: false,
      showCorrectMessage: false,
      showSubmit: true,
      currentScreen: "start",
      timeLeft: 20,
      difficulty: "easy",
    }));

    if (grammarData) {
      shuffleArray(grammarData);
    }
  }

  useEffect(() => {
    console.log("Updated Score:", state.score);
  }, []);

  if (loading) {
    return (
      <div className="loading-screen">
        <h2>Loading grammar questions...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-screen">
        <h2>Error loading grammar questions: {error}</h2>
      </div>
    );
  }

  if (!state.questions || state.questions.length === 0) {
    return (
      <div className="error-screen">
        <h2>No questions available</h2>
      </div>
    );
  }

  return (
    <div className="mingle-full-container">
      <audio 
        ref={audioRefBgm} 
        src={bgm} 
        loop 
      />
      <AudioControls 
        volume={volume} 
        setVolume={setVolume} 
        isMuted={isMuted} 
        setIsMuted={setIsMuted} 
      />
      <img className="background-image" src={mingleBackground} alt="mingle" />
      <div className="mingle-container">
        <div id="mingle-page">
          {state.currentScreen === "start" && (
            <>
              <h1 className="mingle-unit-title">{unit.name}</h1>
              {!showTutorial ? (
                <div id="settings-container" className="mingle-start-card">
                  <button
                    type="button"
                    className="mingle-help-button"
                    onClick={() => setShowTutorial(true)}
                    aria-label="Open Mingle tutorial"
                  >
                    ?
                  </button>
                  <h2>Mingle Game</h2>
                  <div>
                    <label>Difficulty Level:</label>
                    <input
                      type="radio"
                      id="easy"
                      name="difficulty"
                      value="easy"
                    />
                    <label htmlFor="easy">Easy</label>
                    <input
                      type="radio"
                      id="medium"
                      name="difficulty"
                      value="medium"
                    />
                    <label htmlFor="medium">Medium</label>
                    <input
                      type="radio"
                      id="hard"
                      name="difficulty"
                      value="hard"
                    />
                    <label htmlFor="hard">Hard</label>
                  </div>
                  <button onClick={startGame}>Start Game</button>
                </div>
              ) : (
                <div className="mingle-tutorial-card">
                  <h2>Mingle Tutorial</h2>
                  <p>
                    <b>Goal:</b> Survive the mingle round by answering grammar
                    questions correctly before the timer or mistakes eliminate
                    you.
                  </p>
                  <p>
                    <b>How to Play:</b>
                    <br />
                    1. Choose a difficulty level before starting.
                    <br />
                    2. Read the grammar prompt in the center of the screen.
                    <br />
                    3. Select the correct option or True/False response.
                    <br />
                    4. Submit before the timer reaches zero.
                  </p>
                  <p>
                    <b>Rules:</b>
                    <br />
                    Correct answers increase your score.
                    <br />
                    Wrong answers or timeouts count as mistakes.
                    <br />
                    The round ends if you get 3 wrong or reach the required
                    number of correct answers.
                  </p>
                  <p>
                    <b>Difficulty:</b>
                    <br />
                    Easy: 3 correct answers needed, 20 seconds per question.
                    <br />
                    Medium: 5 correct answers needed, 15 seconds per question.
                    <br />
                    Hard: 7 correct answers needed, 10 seconds per question.
                  </p>
                  <button onClick={() => setShowTutorial(false)}>
                    Back to Game Setup
                  </button>
                </div>
              )}
            </>
          )}

          {state.currentScreen === "game" && (
            <div id="game">
              <div id="timer" className={state.timeLeft <= 5 ? "urgent" : ""}>
                Time Left: {state.timeLeft}s
              </div>
              <div id="score-tracker">
                <h3>Score: {state.score}</h3>
              </div>
              <div id="question">
                {state.questions &&
                  state.questions[state.currQuestionIndex] && (
                    <>
                      {state.questions[state.currQuestionIndex].choices
                        .length === 0 ? (
                        <div>
                          <h2>Is the following grammatically true?</h2>
                        </div>
                      ) : (
                        <h2>Choose an option to make it grammatically true</h2>
                      )}
                      <h2>
                        <span id="question-text">
                          {state.questions[state.currQuestionIndex].question}
                        </span>
                      </h2>
                    </>
                  )}
              </div>

              <div>
                {state.questions &&
                  state.questions[state.currQuestionIndex] &&
                  (state.questions[state.currQuestionIndex].choices.length ===
                  0 ? (
                    <div>
                      <input
                        type="radio"
                        name="true_false"
                        id="true"
                        value="true"
                        checked={state.radioButton === "true"}
                        onChange={handleOptionChange}
                      />
                      <label htmlFor="true">True</label>
                      <input
                        type="radio"
                        name="true_false"
                        id="false"
                        value="false"
                        checked={state.radioButton === "false"}
                        onChange={handleOptionChange}
                      />
                      <label htmlFor="false">False</label>
                    </div>
                  ) : (
                    <div>
                      {state.questions[state.currQuestionIndex].choices.map(
                        (choice, i) => (
                          <React.Fragment key={i}>
                            <input
                              type="radio"
                              name="multi_choice"
                              value={choice.text}
                              id={`choice-${i}`}
                              checked={state.radioButton === choice.text}
                              onChange={handleOptionChange}
                            />
                            <label htmlFor={`choice-${i}`}>{choice.text}</label>
                          </React.Fragment>
                        ),
                      )}
                    </div>
                  ))}
              </div>

              <div id="answer">
                {state.showSubmit && (
                  <button className="btn btn-primary" onClick={submitAnswer}>
                    Submit
                  </button>
                )}
              </div>

              {state.showErrorMessage && (
                <div id="error">
                  <h2 id="error-message">Incorrect answer!</h2>
                  <h3 id="error-message">
                    The right answer is :{" "}
                    {state.questions[state.currQuestionIndex].answer}
                  </h3>
                </div>
              )}
              {state.showCorrectMessage && (
                <div id="correct">
                  <h2 id="correct-message">Correct answer! Good job!</h2>
                </div>
              )}
              <img className="feedback-image" src={state.currentImage} alt="" />
            </div>
          )}

          {state.currentScreen === "end" && (
            <div id="postgame">
              {state.score === state.requiredScore ? (
                <h2 id="win-text">Congratulations, you win!</h2>
              ) : (
                <h2 id="lose-text">Sorry, you lose. Better luck next time!</h2>
              )}
              <button className="btn btn-primary" onClick={playAgain}>
                Play Again
              </button>
            </div>
          )}
        </div>

        <Link
          to={GAME_OPTIONS_PATH}
          state={{ unit: unit, category: category }}
          className="game-options-back-link"
        >
          <button className="btn btn-back game-options-back-button">{"Back"}</button>
        </Link>
      </div>
    </div>
  );
}

export default MinglePage;
